import React, { useEffect, useState } from 'react'
import fs from 'fs'
import { BlockFeatureContact, Layout } from '../../src/components'
import { ButtonNew, Cta, HeroIntegration, LogoGrid } from '../../src/storybook'
import BodySection from '../../src/components/integrations/BodySection/body-section'
import { replaceTokens } from '../../src/utils/text'
import { useGlobalData } from '../_app'
import { getUrl } from '../../src/utils/urls'
import { getMicroCopy } from '../../src/utils/microcopy'
import DynamicPage from '../[...slug]'
import {
  getAvailablePaths,
  getAvailablePathsForIntegration,
  getSiteAlternateUrl
} from '../../build-helpers/utils'
import path from 'path'
import { useMicroCopyData } from '@/src/context/MicroCopyContext'
import { getIntegrationfromCMS, getIntegrationSlugs } from '@/build-helpers/integration'
import { getMarketHeader } from '@/build-helpers/market'
import { getCustomerLogosByListOfIds } from '@/build-helpers/customer'
import { getIntegrationCategoryMetaTags, getIntegrationCategorySlugs } from '@/src/utils/contentful'
import { getContentBySlug } from '@/build-helpers/page'
import ErrorBoundary from '@/src/components/errorBoundary/ErrorBoundary'
import { useNavigationData } from '@/src/context/NavigationContext'
import { THEME } from '@/src/utils/common'
import { BUTTON_VARIANT } from '@/src/storybook/ButtonNew/ButtonNew'
import { availableLocales } from '@/src/helpers/availableLocales'

export async function getStaticPaths({ locales }) {
  // Remove default locale from locales
  locales = locales.filter((locale) => locale !== 'default' && availableLocales.includes(locale))

  let paths = []
  const dataIntegrations = await getIntegrationSlugs()
  // Get all available locales from markets

  // Create paths for each integration and each locale
  const newPaths = dataIntegrations.flatMap((integration) => ({
    params: {
      slug: integration.slug || ''
    },
    locale: integration.locale
  }))

  paths.push(...newPaths)

  // Create paths for integration categories and each locale
  const dataIntegrationCategories = await getIntegrationCategorySlugs()
  if (dataIntegrationCategories.length > 0) {
    const categoryPaths = dataIntegrationCategories.flatMap((category) =>
      locales.map((locale) => {
        const language = locale.split('-')[0]
        return {
          params: {
            slug:
              category.slug.find((s) => s.locale === language)?.value ||
              category.slug.find((s) => s.locale === 'en')?.value
          },
          locale: locale
        }
      })
    )
    paths.push(...categoryPaths)
  }

  // If not production, don't build paths
  if (process.env.DISABLE_PATHS_INTEGRATIONS == 1) {
    return {
      paths: [],
      fallback: 'blocking'
    }
  }

  return {
    paths: paths,
    fallback: false // Set fallback to true to render pages on-demand
  }
}

export async function getStaticProps({ params, locale }) {
  if (locale === 'default' || !availableLocales.includes(locale)) {
    return {
      notFound: true
    }
  }

  const { slug } = params
  const slugPrefix = locale
  const language = locale.split('-')[0]

  /* const loadedIntegration = await loadIntegrationsFile(slug, language) */
  const integration = await getIntegrationfromCMS(
    slug,
    language
  ) /* loadedIntegration ? loadedIntegration : null */

  if (!integration) {
    let pageContext = await getMarketHeader(language, slugPrefix, null)

    if (!pageContext || !pageContext.market?.id) {
      return {
        notFound: true
      }
    }

    const pageContent = await getContentBySlug('integrations', language, pageContext.market.id)

    const alternativeHeaderId = pageContent?.alternativeHeader?.id || null

    if (!pageContent) {
      return {
        notFound: true
      }
    }

    if (alternativeHeaderId) {
      const alternativePageContext = await getMarketHeader(
        language,
        slugPrefix,
        alternativeHeaderId
      )
      // Only update pageContext if alternative header fetch was successful
      if (alternativePageContext && alternativePageContext.market?.id) {
        pageContext = alternativePageContext
      }
    }

    const metaTags = await getIntegrationCategoryMetaTags(slug, language)

    if (metaTags) {
      pageContent.metaTags = metaTags.metaTags
      pageContent.marketsMetaTags = null
    }

    return {
      props: {
        locale: language,
        slugPrefix,
        page: {
          ...pageContent
        },
        availablePaths: getAvailablePaths({
          slugLocales: pageContent?._allSlugLocales,
          enabledLocales: pageContent?._allEnabledLocalesLocales,
          enabledMarkets: pageContent?.enabledMarkets,
          integrationCategories: metaTags?.slug
        }),
        header: pageContext?.header,
        market: pageContext?.market,
        footer: pageContext?.footer,
        isIntegration: false
      }
    }
  }

  const pageContext = await getMarketHeader(language, slugPrefix)
  if (!pageContext || !pageContext.market?.id) {
    return {
      notFound: true
    }
  }
  let customersKeys = null
  // Get random customer logos for Logo Grid
  const file = path.join(process.cwd(), 'public', 'paths', `customer.json`)
  if (fs.existsSync(file)) {
    const customersData = await JSON.parse(fs.readFileSync(file, 'utf-8'))
    customersKeys = Object.keys(customersData)
  }
  return {
    props: {
      context: {
        customersKeys: customersKeys
      },
      locale: language,
      slugPrefix,
      page: {
        ...integration
      },
      availablePaths: getAvailablePathsForIntegration({
        slug: integration?.slug,
        enabledMarkets: integration?.markets
      }),
      header: pageContext?.header,
      market: pageContext?.market,
      footer: pageContext?.footer,
      isIntegration: true
    }
  }
}

function getRandomEntries(array, count) {
  if (array.length < count) {
    throw new Error('Array does not have enough items to select from.')
  }

  // Shuffle the array
  const shuffled = array.sort(() => 0.5 - Math.random())

  // Select the first `count` items
  return shuffled.slice(0, count)
}

const Integration = ({ page, slugPrefix, locale, context, availablePaths }) => {
  const ctaTheme = THEME.dark
  const globalData = useGlobalData()
  const { navigationItems } = useNavigationData()
  const microCopyData = useMicroCopyData()
  const { integrationDetailRequestDemoBlock } = globalData.settings || {}
  const [customerLogos, setCustomerLogos] = useState([])

  useEffect(() => {
    const fetchCustomerLogos = async () => {
      if (!context?.customersKeys) return

      // Randomize customers
      const randomizedCustomers = getRandomEntries(context.customersKeys, 6)
      if (!randomizedCustomers) return

      try {
        // Fetch customer logos data
        const customerLogosData = await getCustomerLogosByListOfIds(randomizedCustomers)
        if (!customerLogosData) return

        // Set customer logos
        setCustomerLogos(
          customerLogosData.map((c) => ({
            id: c.id,
            logo: c.logo.url,
            title: c.logo.title
          }))
        )
      } catch (error) {
        console.error('Error fetching customer logos:', error)
      }
    }

    fetchCustomerLogos()
  }, [context?.customersKeys])

  const {
    id,
    title,
    metaTags,
    metaImage,
    marketsMetaTags,
    categories,
    description,
    detailTitle,
    logo,
    slug
  } = page

  const category = categories ? categories[0] : {}

  if (category?.defaultMetaDescription) {
    metaTags.description = category?.defaultMetaDescription
      .find((d) => d.locale === locale)
      ?.value.replace('{integrationName}', title)
  }

  const typeLabel = category
    ? {
        label: category.title,
        url: `/${slugPrefix}/integrations/${category.slug}`,
        icon: category.icon || ''
      }
    : {}

  const requestDemoSlug = getUrl({
    navItem: page.requestADemoAction,
    slugPrefix: slugPrefix,
    navigationItems: navigationItems
  })

  const alternateUrls = Array.isArray(availablePaths)
    ? availablePaths.map(({ slugPrefix }) => {
        const path = `${slugPrefix}/integrations/${slug}`
        return {
          rel: 'alternate',
          hrefLang: slugPrefix,
          href: getSiteAlternateUrl(path)
        }
      })
    : []

  const primaryButtonLabel = getMicroCopy({ key: 'requestDemo', data: microCopyData[locale] })

  return (
    <ErrorBoundary>
      <Layout
        seoData={{
          title,
          marketsMetaTags: marketsMetaTags,
          metaTags: metaTags,
          pageType: 'integration',
          metaImage: metaImage,
          alternate: alternateUrls
        }}
        marketsSlugs={{}}
        allLangSlugs={{}}
        langSlugs={{}}
        slugPrefix={slugPrefix}
        allMarkets={globalData?.markets}
        paths={availablePaths}
      >
        <HeroIntegration
          title={detailTitle || title}
          type={typeLabel}
          integrations={[{ id: `logo-${id}`, label: title, image: logo }]}
          content={description || ''}
          textTokens={{ integration: title }}
          primaryButton={{
            url: requestDemoSlug,
            label: primaryButtonLabel
          }}
        />

        <BodySection integration={page} requestDemoSlug={requestDemoSlug} slugPrefix={slugPrefix} />

        {category && Array.isArray(category?.uspBlocks) && category.uspBlocks.length > 0 ? (
          <BlockFeatureContact data={{ featureCards: category.uspBlocks }} />
        ) : null}

        {customerLogos ? (
          <LogoGrid
            title={getMicroCopy({ key: 'logosBlockTitle', data: microCopyData[locale] })}
            logos={customerLogos}
          />
        ) : null}

        <Cta
          title={replaceTokens(integrationDetailRequestDemoBlock?.title || '', {
            integration: title
          })}
          text={integrationDetailRequestDemoBlock?.description || ''}
          classes={['u-display:none@to:viewport-9']}
          anchor={integrationDetailRequestDemoBlock?.anchor}
          theme={ctaTheme}
        >
          {page.requestADemoAction && (
            <ButtonNew
              url={getUrl({
                navItem: page.requestADemoAction,
                slugPrefix: slugPrefix,
                navigationItems: navigationItems
              })}
              variant={ctaTheme === THEME.dark ? BUTTON_VARIANT.GREEN : BUTTON_VARIANT.PRIMARY}
            >
              {page.requestADemoAction.title || ''}
            </ButtonNew>
          )}
        </Cta>
      </Layout>
    </ErrorBoundary>
  )
}

const Page = (props) => {
  // integration/[integration-slug]
  if (typeof props.isIntegration === 'boolean' && props.isIntegration === true) {
    return <Integration {...props} />
  }
  // integration/[category-slug]
  return <DynamicPage {...props} />
}

export default Page
